import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { MaskText, SectionLabel, Magnetic } from "./primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = ["Brand Identity", "Marketing Strategy", "Social & Content", "Web & Digital", "Film & Motion", "Not sure yet"];
const BUDGETS = ["< $5k", "$5k – $15k", "$15k – $50k", "$50k+"];

const initial = { name: "", email: "", company: "", service: "", budget: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data?.message || "Message sent. We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const chip = (val, cur, onClick, testid) => (
    <button
      type="button"
      key={val}
      data-testid={testid}
      onClick={onClick}
      className="rounded-full px-4 py-2 text-sm font-oswald uppercase tracking-wide transition-colors duration-300 border"
      style={{
        borderColor: cur === val ? "#fff" : "rgba(255,255,255,0.3)",
        background: cur === val ? "rgba(255,255,255,0.16)" : "transparent",
        color: "#fff",
      }}
    >
      {val}
    </button>
  );

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden py-24 md:py-32"
      style={{ background: "var(--dm-teal)" }}
    >
      <div className="grain absolute inset-0 opacity-100" aria-hidden />
      <div className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionLabel tone="maroon" className="text-white/80">Start a project</SectionLabel>
            <MaskText
              lines={["Let's", "talk."]}
              className="mt-4 font-display font-extrabold text-white tracking-tighter leading-[0.85] text-7xl md:text-8xl lg:text-9xl"
            />
            <p className="mt-8 max-w-sm text-white/75 leading-relaxed">
              Tell us what you&rsquo;re building. We&rsquo;ll come back within one working day
              with next steps.
            </p>
            <div className="mt-10 space-y-4 text-white/80">
              <a href="mailto:info@doctor-media.net" data-testid="contact-email" className="block font-display text-xl hover:text-white transition-colors">
                info@doctor-media.net
              </a>
              <div className="flex flex-wrap gap-6 text-sm font-oswald uppercase tracking-wide text-white/60">
                <span>Baghdad, Iraq</span>
                <span>Dubai, UAE</span>
                <span>Amman, Jordan</span>
              </div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <Field label="Your name *" testid="input-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              <Field label="Email *" testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
            </div>
            <Field label="Company" testid="input-company" value={form.company} onChange={set("company")} placeholder="Company / brand" />

            <div>
              <span className="overline text-[11px] text-white/60">Service</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {SERVICES.map((s) => chip(s, form.service, () => setForm((f) => ({ ...f, service: s })), `chip-service-${s}`))}
              </div>
            </div>

            <div>
              <span className="overline text-[11px] text-white/60">Budget</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {BUDGETS.map((b) => chip(b, form.budget, () => setForm((f) => ({ ...f, budget: b })), `chip-budget-${b}`))}
              </div>
            </div>

            <div>
              <span className="overline text-[11px] text-white/60">Project details *</span>
              <textarea
                data-testid="input-message"
                value={form.message}
                onChange={set("message")}
                rows={4}
                placeholder="Tell us about your goals..."
                className="mt-3 w-full bg-transparent border-b border-white/30 focus:border-white outline-none text-white placeholder-white/40 py-3 resize-none transition-colors"
              />
            </div>

            <Magnetic strength={0.4} className="inline-block">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-2 rounded-full px-8 py-4 font-oswald uppercase tracking-[0.12em] text-sm text-white disabled:opacity-70"
                style={{ background: "var(--dm-maroon)" }}
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending</> : <>Send inquiry <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
              </button>
            </Magnetic>
          </form>
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, testid, value, onChange, placeholder, type = "text" }) => (
  <label className="block">
    <span className="overline text-[11px] text-white/60">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-3 w-full bg-transparent border-b border-white/30 focus:border-white outline-none text-white placeholder-white/40 py-3 transition-colors"
    />
  </label>
);
